import { PortalCandidate, Reference } from '../api/onboardingService';

/**
 * Unified Progress Calculation Utility (Hub Frontend)
 * 
 * This utility matches the backend PortalCandidate.getCompletionPercentage() logic exactly.
 * 
 * Progress is calculated based on 10 main steps (new order):
 * 1. Profile
 * 2. Why Church Planting
 * 3. References (with partial credit based on hub contact status)
 * 4. Sending Church
 * 5. Background Check (with partial credit for submitted status)
 * 6. Prepare/Enrich (skipped if not married)
 * 7. Spouse Interview (skipped if not married)
 * 8. Theology
 * 9. Spiritual Health (with partial credit based on submissions)
 * 10. Ministry Transitions
 */

export function calculateOnboardingProgress(candidate: PortalCandidate, references: Reference[] = []): number {
  let completedSteps = 0;
  const totalSteps = 10; // 10 main steps (excluding completion step)
  
  // Step 1: Profile
  if (candidate.profileCompleted) completedSteps++;

  // Step 2: Why Church Planting
  if ((candidate as any).whyChurchPlantingCompleted) completedSteps++;
  
  // Step 3: References - only count as complete if submitted AND all references contacted by hub
  if (candidate.referencesCompleted) {
    if (references.length > 0) {
      const allContactedByHub = references.every(ref => ref.contactedByHub);
      if (allContactedByHub) {
        completedSteps++; // Full credit if all contacted
      } else {
        const contactedCount = references.filter(ref => ref.contactedByHub).length;
        completedSteps += 0.5 + (0.5 * (contactedCount / references.length)); // Partial credit based on how many contacted
      }
    } else {
      // If references submitted but none exist (edge case), give partial credit
      completedSteps += 0.5;
    }
  }
  
  // Step 4: Sending Church
  if (candidate.sendingChurchCompleted) completedSteps++;
  
  // Step 5: Background Check - only count as complete if admin has marked it as 'received'
  if (candidate.backgroundCheckStatus === 'received') {
    completedSteps++;
  } else if (candidate.backgroundCheckStatus === 'submitted') {
    completedSteps += 0.5; // Partial credit if submitted but not received
  }
  
  // Step 6: Prepare/Enrich - if not married, count as complete
  // Otherwise only count as complete if counselor has marked it as 'complete'
  if (candidate.maritalStatus !== 'married') {
    completedSteps++;
  } else if (candidate.prepareEnrichStatus === 'complete') {
    completedSteps++;
  } else if (candidate.prepareEnrichStatus === 'scheduled' || candidate.prepareEnrichStatus === 'started') {
    completedSteps += 0.5; // Partial credit if in progress
  }

  // Step 7: Spouse Interview - if not married, count as complete
  // Otherwise count based on submission/completion status
  if (candidate.maritalStatus !== 'married') {
    completedSteps++;
  } else if ((candidate as any).spouseInterviewStatus === 'complete') {
    completedSteps++;
  } else if ((candidate as any).spouseInterviewSubmitted) {
    completedSteps += 0.5; // Partial credit if submitted but not marked complete
  }

  // Step 8: Theology
  if (candidate.theologyCompleted) completedSteps++;
  
  // Step 9: Spiritual Health - count based on marital status
  // Single candidates need only their own survey
  // Married candidates need both candidate and spouse surveys
  if (candidate.spiritualHealthCompleted) {
    completedSteps++;
  } else if (candidate.maritalStatus !== 'married' && candidate.spiritualHealthCandidateSubmitted) {
    completedSteps += 0.5; // Partial credit if candidate submitted but not marked complete by admin
  } else if (candidate.maritalStatus === 'married') {
    const bothSubmitted = candidate.spiritualHealthCandidateSubmitted && candidate.spiritualHealthSpouseSubmitted;
    const oneSubmitted = candidate.spiritualHealthCandidateSubmitted || candidate.spiritualHealthSpouseSubmitted;
    if (bothSubmitted) {
      completedSteps += 0.5; // Partial credit if both submitted but not marked complete by admin
    } else if (oneSubmitted) {
      completedSteps += 0.25; // Quarter credit if only one submitted
    }
  }

  // Step 10: Ministry Transitions
  if ((candidate as any).ministryTransitionsCompleted) completedSteps++;
  
  return Math.round((completedSteps / totalSteps) * 100);
}

export default calculateOnboardingProgress;
